

import React, { useEffect, useState } from "react";
import { exportBookEntriesToCSV, parseCSVToBookData } from "../utils/documentHandler";
import { BookData, BookEntry, BookMeta } from "../types/bookInvoice";
import { ColumnConfig, GenericTable } from "./shared/GenericTable.";
import { useTranslation } from 'react-i18next';
import { useBookInvoices } from '../hooks/useBookInvoices'
import { generateRandomNumbr } from "../utils/randomNumbers";
import FileUpload from "./shared/FileUpload";
import { Button } from "react-bootstrap";
import { formatToCroatianDate } from "../utils/dateHandler";



const BookInvoiceForm: React.FC = () => {
  const { t } = useTranslation();
  const { addBookInvoice, clearBookInvoices, bookInvoices } = useBookInvoices();
  const [entries, setEntries] = useState<BookEntry[]>([]);
  const [ selectedFile, setSelectedFile ] = useState<File | null>(null);
 
  const [meta, setMeta] = useState<BookMeta>({
    id: generateRandomNumbr(),
    activityName: "",
    activityCode: "",
    entrepreneurName: "",
    address: "",
    oib: "",
    businessUnitName: "",
    businessUnitAddress: "",
  });

  const [newEntry, setNewEntry] = useState<Omit<BookEntry, "id" | "totalAmount">>({
    date: "",
    receiptNumber: "",
    description: "",
    cashAmount: '0.00',
    cashlessAmount: '0.00'
  });

  useEffect(()=>{
    if(bookInvoices){
      bookInvoices.forEach((data) => {
        setMeta(data.meta);
        setEntries(data.entries)
      })
    }    
  }, [bookInvoices])

  const handleAddEntry = () => {

    const entry: BookEntry = {
      id: bookInvoices[0] ? bookInvoices[0].entries[bookInvoices[0].entries.length - 1].id + 1 : 1,
      ...newEntry,
      totalAmount: parseFloat(newEntry.cashAmount + newEntry.cashlessAmount).toFixed(2),
    };
    setEntries([...entries, entry]);
    setNewEntry({ date: "", receiptNumber: "", description: "", cashAmount: '0.00', cashlessAmount: '0.00' });
    saveData(entry, meta);
  };

  const saveData = (entry: BookEntry, bookMeta: BookMeta) => {
    entry.date = formatToCroatianDate(entry.date)
    const newBookData: BookData = {
      meta,
      entries: [...entries, entry] // only this one, remove all previous
    };
    clearBookInvoices()
    addBookInvoice(newBookData);
  }

  const handleChangeMeta = (key: keyof BookMeta, value: string) => {
    setMeta((prev) => ({ ...prev, [key]: value }));
  };

  const handleChangeEntry = (key: keyof typeof newEntry, value: string | number) => {
    setNewEntry((prev) => ({ ...prev, [key]: value }));
  };

  const handleAmount = (key: keyof typeof newEntry, value: string)=> {
    if (/^\d*\.?\d*$/.test(value)) {
      setNewEntry((prev) => ({ ...prev, [key]: value }));
    }
  }

const handleCSVUpload = (file: File) => {
  if (!file) return;

  setSelectedFile(file);
  parseCSVToBookData(file, (bookData) => {
    clearBookInvoices();
    addBookInvoice(bookData);
  });
};
  const bookEntryColumns: ColumnConfig<BookEntry>[] = [
           { label: t('bookInvoice.id'), accessor: 'id' },
           { label: t('bookInvoice.date'), accessor: 'date' },
           { label: t('bookInvoice.description'), accessor: 'description' },
           { label: t('bookInvoice.receiptNumber'), accessor: 'receiptNumber' },
           { label: t('bookInvoice.cashAmount'), accessor: 'cashAmount' },
           { label: t('bookInvoice.cashlessAmount'), accessor: 'cashlessAmount' },
           { label: t('bookInvoice.totalAmount'), accessor: 'totalAmount' },
  ]; 

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="display-5 font-bold mb-4">{t('invoiceBook')}</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <input 
          className="border p-2" 
          placeholder={t('bookInvoice.activityName')} 
          value={meta.activityName} 
          onChange={(e) => handleChangeMeta("activityName", e.target.value)} 
        />
        <input 
          className="border p-2" 
          placeholder={t('bookInvoice.activityCode')} 
          value={meta.activityCode} 
          onChange={(e) => handleChangeMeta("activityCode", e.target.value)} 
        />
        <input 
          style={{minWidth: '170px'}}
          className="border p-2" 
          placeholder={t('bookInvoice.entrepreneurName')} 
          value={meta.entrepreneurName} 
          onChange={(e) => handleChangeMeta("entrepreneurName", e.target.value)} 
        />
        <input 
          className="border p-2" 
          placeholder={t('bookInvoice.address')} 
          value={meta.address} 
          onChange={(e) => handleChangeMeta("address", e.target.value)} 
        />
        <input 
          className="border p-2" 
          placeholder={t('bookInvoice.oib')} 
          value={meta.oib} 
          onChange={(e) => handleChangeMeta("oib", e.target.value)} 
        />
        <input 
          className="border p-2" 
          placeholder={t('bookInvoice.businessUnitName')} 
          value={meta.businessUnitName} 
          onChange={(e) => handleChangeMeta("businessUnitName", e.target.value)} 
        />
        <input 
          className="border p-2" 
          placeholder={t('bookInvoice.businessUnitAddress')} 
          value={meta.businessUnitAddress} 
          onChange={(e) => handleChangeMeta("businessUnitAddress", e.target.value)} 
        />
      </div>

      <div className="mb-6">
        <h3 className="display-7 text-secondary font-semibold mb-6">{t('addToBook')}</h3>
        <div className="d-flex flex-wrap align-items-center ">
          <input 
            className="border p-2" 
            type="date" value={newEntry.date} 
            onChange={(e) => handleChangeEntry("date", e.target.value)} 
          />
          <input 
            className="border p-2" 
            placeholder={t('bookInvoice.receiptNumber')} 
            value={newEntry.receiptNumber} onChange={(e) => handleChangeEntry("receiptNumber", e.target.value)} 
          />
          <input 
            style={{minWidth: '280px'}}
            className="border p-2" 
            placeholder={t('bookInvoice.description')} 
            value={newEntry.description} onChange={(e) => handleChangeEntry("description", e.target.value)} 
          />
          <input 
            style={{minWidth: '270px'}}
            className="border p-2" 
            type="text" 
            inputMode="decimal" 
            placeholder={t('bookInvoice.cashAmount')}  
            value={newEntry.cashAmount === '0.00' ? "" :  newEntry.cashAmount} 
            onChange={(e) => handleAmount("cashAmount", e.target.value)}             
          />
          <input 
            style={{minWidth: '280px'}}
            className="border p-2" 
            type="text" 
            placeholder={t('bookInvoice.cashlessAmount')} 
            value={newEntry.cashlessAmount === '0.00' ? "" : newEntry.cashlessAmount} 
            onChange={(e) => handleAmount("cashlessAmount", e.target.value)} 
          />
          <Button 
           variant="primary"
           className="w-100 w-auto d-flex justify-content-center align-items-center my-2 ms-1" 
           onClick={handleAddEntry} 
           style={{height: '35px'}}
          >
          {t('addInvoice')} 
         </Button>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2">
         <Button 
           variant="primary"
           className="w-100 w-auto d-flex justify-content-center align-items-center my-2" 
           onClick={() => exportBookEntriesToCSV(entries, meta)} 
           style={{height: '35px'}}
          >
          {t('exportToExcel')}
         </Button>
       
        <FileUpload onFileUpload={(file) => handleCSVUpload(file)} />
        {selectedFile && <div>{t('fileName')}: {selectedFile.name}</div>}
      </div>

      {entries.length > 0 && (
        <GenericTable
          data={entries}
          columns={bookEntryColumns}
        />
      )}
    </div>
  );
};

export default BookInvoiceForm;

